import {
    ReactNode,
    createContext,
    useContext,
    useEffect,
    useState,
} from "react";
import { LinkedAccountResponse } from "@/services/marketplace/types";

import { useOrganizationContext } from "./OrganizationContext";
import { marketplaceService } from "@/services/marketplace/MarketplaceService";
import { GetContract } from "@/services/fhir/contract/GetContract";
import { GetPaymentNotice } from "@/services/fhir/financial/GetPaymentNotice";

interface FluxPayContractContextValue {
    hasFluxPayExtension: boolean;
    contractId: string;
    refetchExtensionStatus: () => void;
    contractFluxPay: any | null;
    isLoadingContract: boolean;
    paymentNoticeFluxPay: any | null;
    isLoadingPaymentNoticeFluxPay: boolean;
    fetchPaymentNoticeFluxPay: () => Promise<void>;
    additionalInformation: {
        nome: string;
        cpfCnpj: string;
        email: string;
    } | null;
    isLoadingAdditionalInformation: boolean;
}
  
export const FluxPayContractContext = createContext<FluxPayContractContextValue>(null!);

export function FluxPayContractContextProvider({ children }: { children: ReactNode }) {
    const { organization } = useOrganizationContext();

    const [hasFluxPayExtension, setHasFluxPayExtension] = useState(false);
    const [contractId, setContractId] = useState("");
    const [refetchTrigger, setRefetchTrigger] = useState(false);
    const [contractFluxPay, setContractFluxPay] = useState<any | null>(null);
    const [isLoadingContract, setIsLoadingContract] = useState(false);
    const [paymentNoticeFluxPay, setPaymentNoticeFluxPay] = useState<any | null>(null);
    const [isLoadingPaymentNoticeFluxPay, setIsLoadingPaymentNoticeFluxPay] = useState(false);
    const [additionalInformation, setAdditionalInformation] = useState<{
        nome: string;
        cpfCnpj: string;
        email: string;
    } | null>(null);
    const [isLoadingAdditionalInformation, setIsLoadingAdditionalInformation] = useState(false);

    const refetchExtensionStatus = () => {
        setRefetchTrigger(prev => !prev);
    };

    const fetchPaymentNoticeFluxPay = async () => {
            setPaymentNoticeFluxPay(null);

        try {
            setIsLoadingPaymentNoticeFluxPay(true);
            const paymentData = await GetPaymentNotice({
                identifier: `Organization/${organization?.id}`,
                paymentStatus: "cleared"
            });
            setPaymentNoticeFluxPay(paymentData?.entry);
        } catch (error) {
            console.error("Erro ao buscar payment notice:", error);
            setPaymentNoticeFluxPay(null);
        } finally {
            setIsLoadingPaymentNoticeFluxPay(false);
        }
    };

    useEffect(() => {
        const checkExtensionStatus = async () => {
            if (!organization?.id) return;

            try {
                const extensionData = await marketplaceService.checkMarketplaceExtension(organization.id);
                setHasFluxPayExtension(extensionData.hasActiveExtension);
                setContractId(extensionData.contractId || "");
            } catch (error) {
                console.error("Erro ao verificar status da extensão FluxPay:", error);
                setHasFluxPayExtension(false);
                setContractId("");
            }
        };
        checkExtensionStatus();
    }, [organization?.id, refetchTrigger]);

    useEffect(() => {
        const fetchContract = async () => {
            if (!contractId) {
                setContractFluxPay(null);
                return;
            }

            try {
                setIsLoadingContract(true);
                const contractData = await GetContract({
                    id: contractId,
                });
                setContractFluxPay(contractData);
            } catch (error) {
                console.error("Erro ao buscar contrato:", error);
                setContractFluxPay(null);
            } finally {
                setIsLoadingContract(false);
            }
        };

        fetchContract();
    }, [contractId]);

    useEffect(() => {
        if (contractId) {
            fetchPaymentNoticeFluxPay();
        }
    }, [contractId]);

    useEffect(() => {
        const fetchLinkedAccount = async () => {
            if (!contractFluxPay || !organization?.id) return;
            
            try {
                setIsLoadingAdditionalInformation(true);
                const linkedAccountData: LinkedAccountResponse = await marketplaceService.linkedAccount(organization.id);
                
                if (linkedAccountData.success && linkedAccountData.data) {
                    setAdditionalInformation({
                        nome: linkedAccountData.data.identification.type === "CPF" 
                            ? `${linkedAccountData.data.first_name} ${linkedAccountData.data.last_name}` 
                            : linkedAccountData.data.company?.corporate_name || "",
                        cpfCnpj: linkedAccountData.data.identification.number,
                        email: linkedAccountData.data.email
                    });
                } else {
                    setAdditionalInformation(null);
                }
            } catch (error) {
                console.error("Erro ao buscar informações da conta vinculada:", error);
                setAdditionalInformation(null);
            } finally {
                setIsLoadingAdditionalInformation(false);
            }
        };

        fetchLinkedAccount();
    }, [contractFluxPay, organization?.id]);

    const contextValue = {
        hasFluxPayExtension,
        contractId,
        refetchExtensionStatus,
        contractFluxPay,
        isLoadingContract,
        paymentNoticeFluxPay,
        isLoadingPaymentNoticeFluxPay,
        fetchPaymentNoticeFluxPay,
        additionalInformation,
        isLoadingAdditionalInformation,
    };

    return (
        <FluxPayContractContext.Provider value={contextValue}>
            {children}
        </FluxPayContractContext.Provider>
    );
}
  
export function useFluxPayContractContext() {
    return useContext(FluxPayContractContext);
}
  